import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Chip,
  FormHelperText,
  Alert,
} from "@mui/material";
import { submitVolunteerApplication } from "../../apis/user-apis/VolunteerSectionApis";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
};

const volunteerOptions = [
  {
    icon: "👥",
    title: "Canvass Your Neighborhood",
    desc: "The most impactful thing you can do. We'll train you and send you out with materials.",
  },
  {
    icon: "📍",
    title: "Request a Yard Sign",
    desc: "Show your neighbors where you stand. Signs across Middletown streets signal momentum.",
  },
  {
    icon: "📱",
    title: "Phone Banking",
    desc: "Call identified supporters from home. Training provided.",
  },
  {
    icon: "✉️",
    title: "Spread the Word",
    desc: "Share Gary's message with your social network. Every share reaches more neighbors.",
  },
];

const interests = [
  "Canvassing",
  "Phone Banking",
  "Yard Sign",
  "Events",
  "Social Media",
  "Donations",
];

const NAME_REGEX = /^[\p{L}\p{M}\s'-’]+$/u;
const filterName = (value) => value.replace(/[^\p{L}\p{M}\s'-’]/gu, "");

const VolunteerSection = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    zipCode: "",
    interests: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Inline success/error message — replaces top-right Snackbar
  const [inlineMsg, setInlineMsg] = useState({
    show: false,
    text: "",
    severity: "success",
  });

  const handleChange = (field, value) => {
    if (field === "fullName") value = filterName(value);
    if (field === "phone") value = value.replace(/\D/g, "").slice(0, 10);
    if (field === "zipCode") value = value.replace(/\D/g, "").slice(0, 6);
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleInterestToggle = (interest) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const nameTrimmed = formData.fullName.trim();
    if (!nameTrimmed) {
      newErrors.fullName = "Full name is required";
    } else if (nameTrimmed.split(/\s+/).filter(Boolean).length < 2) {
      newErrors.fullName = "Please enter full name";
    } else if (!NAME_REGEX.test(nameTrimmed)) {
      newErrors.fullName = "Enter a valid name";
    }

    if (!formData.email.trim()) newErrors.email = "Email address is required";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email))
      newErrors.email = "Enter a valid email";
    if (!formData.zipCode.trim()) newErrors.zipCode = "Zip code is required";
    else if (formData.zipCode.length > 6)
      newErrors.zipCode = "Zip code cannot exceed 6 digits";
    if (formData.phone && formData.phone.length !== 10)
      newErrors.phone = "Phone number should be 10 digits";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);
    if (!validateForm()) return;
    const nameParts = formData.fullName.trim().split(" ");
    const payload = {
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(1).join(" ") || "",
      email: formData.email,
      phone: formData.phone,
      zipCode: formData.zipCode,
      interests: formData.interests,
      availability: [],
      status: "new",
    };
    try {
      setLoading(true);
      const response = await submitVolunteerApplication(payload);
      // Show inline success message below the button
      setInlineMsg({
        show: true,
        text: response?.message || "Application submitted successfully!",
        severity: "success",
      });
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        zipCode: "",
        interests: [],
      });
      setHasSubmitted(false);
      // Auto-hide after 6 seconds
      setTimeout(
        () => setInlineMsg((prev) => ({ ...prev, show: false })),
        6000,
      );
    } catch (error) {
      setInlineMsg({
        show: true,
        text: error?.message || "Something went wrong",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      id="volunteer"
      sx={{ bgcolor: "#FDFBF7", py: { xs: 6, md: 8 }, width: "100%" }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 6, md: 10 },
            alignItems: "flex-start",
          }}
        >
          {/* ── Left Column ───────────────────────────────────── */}
          <Box sx={{ flex: 1.1 }}>
            <Typography
              sx={{
                color: "#C59324",
                fontWeight: 700,
                letterSpacing: "0.2em",
                fontSize: "0.85rem",
                textTransform: "uppercase",
                mb: 2,
              }}
            >
              Get Involved
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" },
                color: "#051328",
                fontWeight: 600,
                lineHeight: 1.1,
                mb: 2,
                fontFamily: "'Playfair Display', serif",
              }}
            >
              Neighbors Talking{" "}
              <Box
                component="span"
                sx={{
                  color: "#C59324",
                  fontFamily: "'Playfair Display', serif",
                  fontStyle: "italic",
                  fontWeight: 500,
                }}
              >
                to Neighbors
              </Box>
            </Typography>

            <Typography
              sx={{
                color: "#5D6785",
                fontSize: "0.9rem",
                mb: 5,
                maxWidth: "500px",
              }}
            >
              This campaign wins by showing up in the community. Whether you
              have five minutes or five hours, there's a place for you.
            </Typography>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 6 }}
            >
              {volunteerOptions.map((opt, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 2.5,
                    alignItems: "flex-start",
                    borderBottom: "1px solid #F0F0F0",
                    pb: 2,
                  }}
                >
                  <Typography sx={{ fontSize: "1.2rem", mt: 0.2 }}>
                    {opt.icon}
                  </Typography>
                  <Box>
                    <Typography
                      sx={{
                        color: "#051328",
                        fontWeight: 800,
                        fontSize: "0.85rem",
                        mb: 0.3,
                      }}
                    >
                      {opt.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#5D6785",
                        fontSize: "0.8rem",
                        lineHeight: 1.5,
                      }}
                    >
                      {opt.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* ── Pledge box — "Pledge to Vote" → #petition ──── */}
            <Box
              sx={{
                bgcolor: "#051328",
                color: "#FFF",
                p: 2.5,
                borderRadius: "6px",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
              }}
            >
              <Typography
                sx={{ fontSize: "0.75rem", opacity: 0.9, lineHeight: 1.5 }}
              >
                🗳️ Not ready to volunteer? Take the pledge: commit to voting for
                common-sense leadership in November.
              </Typography>
              <Button
                onClick={() => scrollTo("#petition")}
                sx={{
                  bgcolor: "#C59324",
                  color: "#000",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  px: 3,
                  py: 1,
                  "&:hover": { bgcolor: "#A67C1E" },
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Pledge to Vote
              </Button>
            </Box>
          </Box>

          {/* ── Right Column: Form ────────────────────────────── */}
          <Box sx={{ flex: 0.9, width: "100%" }}>
            <Box
              sx={{
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: "8px",
                p: { xs: 3, md: 4 },
                bgcolor: "#fff",
              }}
            >
              <Typography
                sx={{
                  color: "#051328",
                  fontSize: "1.3rem",
                  fontWeight: 700,
                  mb: 3,
                }}
              >
                Join the Team
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Full Name */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      color: "#051328",
                      mb: 0.8,
                    }}
                  >
                    YOUR FULL NAME*
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.fullName}
                    placeholder="Enter your name"
                    onChange={(e) => handleChange("fullName", e.target.value)}
                    error={hasSubmitted && !!errors.fullName}
                    sx={{ bgcolor: "#fdfaf4" }}
                  />
                  {hasSubmitted && errors.fullName && (
                    <FormHelperText error>{errors.fullName}</FormHelperText>
                  )}
                </Box>

                {/* Email */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      color: "#051328",
                      mb: 0.8,
                    }}
                  >
                    EMAIL ADDRESS*
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.email}
                    placeholder="Enter your email"
                    onChange={(e) => handleChange("email", e.target.value)}
                    error={hasSubmitted && !!errors.email}
                    sx={{ bgcolor: "#fdfaf4" }}
                  />
                  {hasSubmitted && errors.email && (
                    <FormHelperText error>{errors.email}</FormHelperText>
                  )}
                </Box>

                {/* Phone */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      color: "#051328",
                      mb: 0.8,
                    }}
                  >
                    PHONE (for GOTV texts)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.phone}
                    placeholder="Enter your phone number"
                    onChange={(e) => handleChange("phone", e.target.value)}
                    error={hasSubmitted && !!errors.phone}
                    sx={{ bgcolor: "#fdfaf4" }}
                  />
                  {hasSubmitted && errors.phone && (
                    <FormHelperText error>{errors.phone}</FormHelperText>
                  )}
                </Box>

                {/* Zip Code */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      color: "#051328",
                      mb: 0.8,
                    }}
                  >
                    ZIP CODE*
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.zipCode}
                    placeholder="Enter your zip code"
                    onChange={(e) => handleChange("zipCode", e.target.value)}
                    error={hasSubmitted && !!errors.zipCode}
                    sx={{ bgcolor: "#fdfaf4" }}
                  />
                  {hasSubmitted && errors.zipCode && (
                    <FormHelperText error>{errors.zipCode}</FormHelperText>
                  )}
                </Box>

                {/* Interests */}
                <Box sx={{ mt: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      color: "#051328",
                      mb: 1.5,
                    }}
                  >
                    I'M INTERESTED IN
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}
                  >
                    {interests.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        clickable
                        onClick={() => handleInterestToggle(tag)}
                        variant={
                          formData.interests.includes(tag)
                            ? "filled"
                            : "outlined"
                        }
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          height: "24px",
                          bgcolor: formData.interests.includes(tag)
                            ? "#C59324"
                            : "transparent",
                          color: formData.interests.includes(tag)
                            ? "#FFF"
                            : "#051328",
                          "&:hover": {
                            color: "#000",
                            borderColor: "#000",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  onClick={handleSubmit}
                  sx={{
                    bgcolor: "#C59324",
                    color: "#FFF",
                    fontWeight: 700,
                    py: 1.5,
                    fontSize: "0.9rem",
                    textTransform: "none",
                    "&:hover": { bgcolor: "#A67C1E" },
                  }}
                >
                  {loading ? "Submitting..." : "✈ Join the Team"}
                </Button>

                {/* ── Inline success/error message ─────────────── */}
                {inlineMsg.show && (
                  <Alert
                    severity={inlineMsg.severity}
                    variant="filled"
                    onClose={() =>
                      setInlineMsg((prev) => ({ ...prev, show: false }))
                    }
                    sx={{ mt: 1 }}
                  >
                    {inlineMsg.text}
                  </Alert>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default VolunteerSection;
